import { Loader } from '@googlemaps/js-api-loader';

const loader = new Loader({
  apiKey: window.themeData.googleMapsApiKey,  // set this in .env
  version: 'weekly',
  libraries: ['places'],                             // add more if needed
});

loader.load().then(() => {
  initMap();   // Google JS API is guaranteed to be on window.google here
});

let map;
let markerInstances = [];
let allMarkersData = [];
let lastViewport = {};

function initMap() {
  const mapElement = document.getElementById('map');
  if (!mapElement) return;

  const iconBase = themeData.iconBase;
  
  // Utilize a separate function for map styling
  const mapStyles = [
    { featureType: 'all', elementType: 'all', stylers: [{ saturation: 32 }, { lightness: -3 }, { visibility: 'on' }, { weight: 1.18 }] },
    { featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'on' }] },
    { featureType: 'landscape', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'landscape.man_made', elementType: 'all', stylers: [{ saturation: -70 }, { lightness: 14 }] },
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'all', stylers: [{ saturation: 100 }, { lightness: -14 }] },
    { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }, { lightness: 12 }] }
  ];  

  const markersJson = mapElement.dataset.markers;
  const hasMultipleMarkers = !!markersJson;
  const mapOptions = {
    zoom: 12,
    styles: mapStyles,
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    zoomControl: true,
    streetViewControl: false,
    scrollwheel: false,
    draggable: true,
    mapTypeControl: false 
  };

  if (hasMultipleMarkers) {
    allMarkersData = JSON.parse(markersJson);
    if (!allMarkersData.length) return;

    const bounds = new google.maps.LatLngBounds();
    map = new google.maps.Map(mapElement, {
      ...mapOptions,
      center: { lat: 0, lng: 0 },
      zoom: 15
    });

    // Initial render with all pins
    renderMarkers(getFilteredMarkers());
    fitMapToMarkers(getFilteredMarkers());

    map.addListener('idle', handleViewportChange);

    // County filter
    const countyFilter = document.querySelector('[name="form_county"]');
    if (countyFilter) {
      countyFilter.addEventListener('change', () => {
        renderMarkers(getFilteredMarkers());
        fitMapToMarkers(getFilteredMarkers());
        fireVisibleMarkersEvent(); // if you want to update visible markers immediately after filter
      });

      const filterForm = countyFilter.form;
      if (filterForm) {
        filterForm.addEventListener('reset', function () {
          setTimeout(() => {
            renderMarkers(getFilteredMarkers());
            fitMapToMarkers(getFilteredMarkers());
            fireVisibleMarkersEvent();
          }, 0);
        });
      }
    }
  } else {
    const lat = parseFloat(mapElement.dataset.lat);
    const lng = parseFloat(mapElement.dataset.lng);
    if (!lat || !lng) return;

    mapOptions.center = { lat, lng };
    map = new google.maps.Map(mapElement, mapOptions);

    new google.maps.Marker({
      map,
      position: { lat, lng },
    });
  }
};

function getFilteredMarkers() {
  const countyFilter = document.querySelector('[name="form_county"]');
  const selectedCounty = countyFilter ? countyFilter.value : '';
  if (!selectedCounty) return allMarkersData;
  return allMarkersData.filter(marker =>
    Array.isArray(marker.counties) && marker.counties.includes(selectedCounty)
  );
}

function renderMarkers(markersData) {
  // Remove existing markers from map
  markerInstances.forEach(marker => marker.setMap(null));
  markerInstances = [];

  // InfoWindow (only one open at a time)
  let infoWindow = null;

  markersData.forEach(markerData => {
    const { lat, lng, title, address, url } = markerData;
    const position = { lat: parseFloat(lat), lng: parseFloat(lng) };
    const marker = new google.maps.Marker({
      map,
      position,
      title,
      // icon: iconBase + 'map-pin.png' // Uncomment if you want custom icons
    });

    marker.addListener('click', () => {
      // Close previous InfoWindow
      if (infoWindow) infoWindow.close();

      // Compose content (add link if you have URLs later)
      const content = `
        <div class="map__tooltip">
          <strong class="map__title">${title}</strong><br>
          ${url ? `<a href="${url}" target="_blank">See Venue</a>` : ''}
        </div>
      `;

      infoWindow = new google.maps.InfoWindow({ content });
      infoWindow.open(map, marker);
    });

    markerInstances.push(marker);
  });
}

function fitMapToMarkers(markersData) {
  if (!markersData.length) return;
  const bounds = new google.maps.LatLngBounds();
  markersData.forEach(({ lat, lng }) => {
    bounds.extend(new google.maps.LatLng(parseFloat(lat), parseFloat(lng)));
  });
  if (markersData.length === 1) {
    // If only one marker, set center and zoom instead
    map.setCenter(bounds.getCenter());
    map.setZoom(14);
  } else {
    map.fitBounds(bounds);
  }
}

function getVisibleMarkerIds() {
  const bounds = map.getBounds();
  if (!bounds) return [];
  const { north, east, south, west } = bounds.toJSON();
  const markersData = getFilteredMarkers();

  return markersData
    .filter(({ lat, lng }) =>
      lat >= south && lat <= north && lng >= west && lng <= east
    )
    .map(marker => marker.id);
}

function fireVisibleMarkersEvent() {
  const visibleIds = getVisibleMarkerIds();
  document.dispatchEvent(
    new CustomEvent('mapVisibleMarkersChanged', { detail: visibleIds })
  );
}

function handleViewportChange() {
  const searchToggle = document.getElementById('map-searchable');
  if (!searchToggle?.checked) return;

  const visibleIds = getVisibleMarkerIds();

  if (
    Array.isArray(lastViewport.visibleIds) &&
    lastViewport.visibleIds.length === visibleIds.length &&
    lastViewport.visibleIds.every((v, i) => v === visibleIds[i])
  ) {
    return; // no change
  }

  lastViewport.visibleIds = visibleIds;

  document.dispatchEvent(
    new CustomEvent('mapVisibleMarkersChanged', { detail: visibleIds })
  );
}
